import React, { useCallback, useRef, useState } from "react";

export default function useDirtyState<T>(
  initialValue: T,
  initialIsDirty = false,
) {
  const [value, setValue] = useState<T>(initialValue);
  const isDirtyRef = useRef(initialIsDirty);
  const [isDirty, setIsDirty] = useState(initialIsDirty);
  const modifyValue = useCallback(
    (newValue: React.SetStateAction<T>, markDirty = true) => {
      setValue(newValue);
      setIsDirty(markDirty);
      isDirtyRef.current = markDirty;
    },
    [setValue, setIsDirty],
  );
  const clean = useCallback(
    (cleanValue: T) => {
      setValue(cleanValue);
      setIsDirty(false);
      isDirtyRef.current = false;
    },
    [setValue, setIsDirty],
  );

  return {
    value,
    modifyValue,
    isDirty: isDirty, // In sync with value
    isDirtyRef, // Useful for blockers that need to know dirty state before a layout update (isDirty lags behind). Keep in mind that value will be in sync with isDirty, but can also lag behind isDirtyRef.
    clean,
  };
}
